import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AiMedicalDocumentItem, AiMedicalDocumentPayload, AppointmentType, DocumentDetailsControls, HealthManagerTagModel, MedicalDocumentsService } from '@lib/medical-documents';
import { TranslatePipe } from '@ngx-translate/core';

import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { Visit } from '@lib/core/models/visit.interface';

import { MD_LIB_CONFIG } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import { InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

@Component({
  standalone: true,
  selector: 'md-document-details-form',
  imports: [
    TranslatePipe,
    MatIcon,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
    MatLabel,
    MatCheckbox,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatDatepickerModule,
    StatusComponent,
    InfoBadgeComponent,
  ],
  templateUrl: './document-details-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDetailsFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly mdService = inject(MedicalDocumentsService);
  private readonly mdConfig = inject(MD_LIB_CONFIG);

  readonly userAppointments = input.required<Visit[]>();
  readonly currentAiDocument = input<AiMedicalDocumentItem | null>(null);
  readonly detailsClosed = output<boolean>();
  readonly formSubmitted = output<AiMedicalDocumentItem>();
  readonly formCancelled = output<boolean>();

  protected readonly formatDate = formatDate;
  protected readonly documentTypes: AppointmentType[] = [
    AppointmentType.Unknown,
    AppointmentType.Appointment,
    // TODO: uncomment when Vaccination will added as one of possible medical document
    // AppointmentType.Drugs,
    AppointmentType.InstrumentalDiagnostic,
    AppointmentType.LaboratoryService,
    AppointmentType.Other,
    AppointmentType.PersonalDoctorAppointment,
  ];

  protected documentDetailsForm!: FormGroup<DocumentDetailsControls>;

  protected readonly healthManagerTags = signal<HealthManagerTagModel[]>([]);
  protected readonly userId = signal<string | null>(null);

  protected readonly formattedFileName = computed<string>(() => this.currentAiDocument().fileName.replace(/-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ''));

  protected get fc(): DocumentDetailsControls {
    return this.documentDetailsForm?.controls;
  }

  protected get isDocumentBoundToUser(): boolean {
    return this.currentAiDocument()?.userId != null && this.currentAiDocument()?.userId !== '00000000-0000-0000-0000-000000000000';
  }

  constructor() {
    effect(() => {
      if (this.documentDetailsForm && this.currentAiDocument()) this.patchFormFromAiDocument();
    });
  }

  ngOnInit() {
    this.userId.set(this.mdConfig.userId);
    this.loadHealthManagerTags();

    if (this.currentAiDocument()) this.patchFormFromAiDocument();
    else this.initForm();
  }

  private loadHealthManagerTags() {
    this.mdService.getHealthManagerTags().subscribe({
      next: tags => this.healthManagerTags.set(tags),
      error: error => console.error('Error loading health manager tags:', error),
    });
  }

  protected initForm(): void {
    this.documentDetailsForm = this.fb.group<DocumentDetailsControls>({
      documentName: this.fb.control(null, [Validators.required]),
      type: this.fb.control(null, [Validators.required]),
      appointment: this.fb.control(null),
      documentDate: this.fb.control(null, [Validators.required]),
      documentTags: this.fb.control(null, [Validators.required]),
      belongsToUser: this.fb.control(this.isDocumentBoundToUser, this.isDocumentBoundToUser ? [] : [Validators.requiredTrue]),
    });
  }

  protected patchFormFromAiDocument() {
    const aiDoc = this.currentAiDocument();

    this.documentDetailsForm = this.fb.group<DocumentDetailsControls>({
      documentName: this.fb.control(aiDoc?.documentTitle || '', [Validators.required]),
      type: this.fb.control(aiDoc?.type || null, [Validators.required]),
      appointment: this.fb.control(this.userAppointments()?.find(item => item.id === aiDoc?.appointmentId)),
      documentDate: this.fb.control(aiDoc?.documentDate ? new Date(aiDoc.documentDate) : null, [Validators.required]),
      documentTags: this.fb.control(aiDoc?.healthManagerTags || [], [Validators.required]),
      belongsToUser: this.fb.control(this.isDocumentBoundToUser, this.isDocumentBoundToUser ? [] : [Validators.requiredTrue]),
    });
  }

  protected submitDetailsForm() {
    const currentDoc = this.currentAiDocument();
    const payload: AiMedicalDocumentPayload = {
      documentTitle: this.fc.documentName.value,
      documentDate: this.fc.documentDate.value,
      type: this.fc.type.value,
      healthManagerTags: [...this.fc.documentTags.value],
      appointmentId: this.fc.appointment.value?.id || null,
      description: this.fc.documentName.value,
    };
    const ownershipConfirmed = this.fc.belongsToUser.value;

    return this.mdService.updateMedicalDocumentDetails(currentDoc.userId, currentDoc.id, payload, ownershipConfirmed).subscribe({
      next: (response: AiMedicalDocumentItem) => this.formSubmitted.emit(response),
      error: error => console.error('Error updating document:', error),
    });
  }

  protected hideDetails() {
    this.detailsClosed.emit(true);
    this.documentDetailsForm.reset();
  }

  protected cancelForm() {
    this.formCancelled.emit(true);
    this.documentDetailsForm.reset();
  }

  protected removeTag(tagToRemove: string, event: Event) {
    event.stopPropagation();
    const updatedTags = (this.fc.documentTags.value || []).filter(tag => tag !== tagToRemove);
    this.fc.documentTags.setValue(updatedTags);
  }
}
